import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../Redux/Store';
import { addMenuOption, deleteMenuOption, fetchMenuOptions } from '../../../Redux/MenuOption/menuOptionSlice';
import { MenuOption, PriceType } from '../../../Redux/MenuOption/types';

interface MenuOptionManagementProps {
  storeId: string;
}

const priceTypes = [
  { value: PriceType.DISCOUNT, label: '値引き' },
  { value: PriceType.ADDITIONAL, label: '増額' },
] as const;

const MenuOptionManagement: React.FC<MenuOptionManagementProps> = ({ storeId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [newOptionName, setNewOptionName] = useState('');
  const [newOptionPrice, setNewOptionPrice] = useState<number>(0);
  const [priceType, setPriceType] = useState<PriceType>(PriceType.DISCOUNT);
  const [error, setError] = useState<string | null>(null);
  const [hasPriceInfo, setHasPriceInfo] = useState(false);
  const [expandedPrice, setExpandedPrice] = useState(false);

  const menuOptions = useSelector((state: RootState) => state.menuOption.menuOptions) || [];
  const loading = useSelector((state: RootState) => state.menuOption.loading);

  useEffect(() => {
    if (storeId) {
      dispatch(fetchMenuOptions(storeId)).catch((error: Error) => setError(error.message));
    }
  }, [dispatch, storeId]);

  const handleAddOption = async () => {
    if (!newOptionName.trim()) {
      setError('オプション名を入力してください');
      return;
    }

    try {
      await dispatch(
        addMenuOption({
          storeId,
          name: newOptionName,
          price: hasPriceInfo ? newOptionPrice : null,
          priceType: hasPriceInfo ? priceType : null,
          sortIndex: menuOptions.length,
        })
      ).unwrap();
      // 追加後に最新のオプション一覧を取得
      await dispatch(fetchMenuOptions(storeId));
      setNewOptionName('');
      setNewOptionPrice(0);
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    }
  };

  const handleDeleteOption = async (optionId: string) => {
    try {
      await dispatch(deleteMenuOption({ storeId, id: optionId })).unwrap();
      // 削除後に最新のメニューオプション一覧を取得
      await dispatch(fetchMenuOptions(storeId));
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : String(error));
    }
  };

  const handlePriceInfoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHasPriceInfo(event.target.checked);
    setExpandedPrice(event.target.checked);
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          オプションの追加
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="オプション名"
            value={newOptionName}
            onChange={(e) => setNewOptionName(e.target.value)}
            error={!!error}
            helperText={error}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={hasPriceInfo}
                onChange={handlePriceInfoChange}
              />
            }
            label="価格情報を設定する"
          />
          <Accordion
            expanded={expandedPrice && hasPriceInfo}
            onChange={() => setExpandedPrice(!expandedPrice)}
            disabled={!hasPriceInfo}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>価格情報</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="価格"
                  type="number"
                  value={newOptionPrice}
                  onChange={(e) => setNewOptionPrice(Number(e.target.value))}
                  fullWidth
                />
                <TextField
                  select
                  label="価格タイプ"
                  value={priceType}
                  onChange={(e) => setPriceType(e.target.value as PriceType)}
                  fullWidth
                >
                  {priceTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </AccordionDetails>
          </Accordion>
          <Button variant="contained" onClick={handleAddOption} disabled={loading}>
            追加
          </Button>
        </Box>
      </Box>

      <Typography variant="h6" gutterBottom>
        オプション一覧
      </Typography>
      <List>
        {Array.isArray(menuOptions) && menuOptions.map((option: MenuOption) => (
          option && option.id && (
            <ListItem key={option.id}>
              <ListItemText
                primary={option.name || '名称未設定'}
                secondary={option.price !== undefined && option.priceType ? 
                  `¥${option.price} (${option.priceType === PriceType.DISCOUNT ? '値引き' : '増額'})` : 
                  undefined}
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => handleDeleteOption(option.id)} disabled={loading}>
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          )
        ))}
      </List>
    </Box>
  );
};

export default MenuOptionManagement;
